import type { Currency } from '@witmetrics/utils';
import {
  formatCurrency,
  formatNumber,
  formatPercentage,
  formatPrice,
} from '@witmetrics/utils/strings';
import { isNullish } from '@witmetrics/utils/misc';
import { isNumber } from '@witmetrics/utils/numbers';
import type { MetricDefinition, MetricDelta, MetricRow } from '@/types/metrics';
import { METRIC_VALUE_KIND } from '@/constants/metrics';

const { RATE, PRICE, COMPACT_PRICE, DURATION } = METRIC_VALUE_KIND;

const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_DAY = 86400;
const MINUS = '−';

function formatSignedNumber(value: number, suffix: string) {
  const sign = value < 0 ? MINUS : '+';
  return `${sign}${formatNumber(Math.abs(value), 1)}${suffix}`;
}

export function buildDelta(
  { kind, lowerIsBetter }: MetricDefinition,
  current: number | null,
  previous: number | null
): MetricDelta {
  if (!isNumber(current) || !isNumber(previous)) return null;
  const isRate = kind === RATE;
  if (!isRate && previous === 0) return null;
  const value = isRate
    ? (current - previous) * 100
    : ((current - previous) / previous) * 100;
  const label = formatSignedNumber(value, isRate ? 'pt' : '%');
  if (Math.abs(value) < 0.05) return { label, tone: 'neutral' };
  const isImprovement = lowerIsBetter ? value < 0 : value > 0;
  return { label, tone: isImprovement ? 'positive' : 'negative' };
}

export function getBarWidth(value: number | null, max: number) {
  if (!value || max <= 0) return 0;
  return Math.round((value / max) * 100);
}

export function buildMetricRow<K extends string>(
  definition: MetricDefinition<K>,
  current: number | null,
  previous: number | null
): MetricRow<K> {
  const max = Math.max(current ?? 0, previous ?? 0);
  return {
    ...definition,
    current,
    previous,
    delta: buildDelta(definition, current, previous),
    currentWidth: getBarWidth(current, max),
    previousWidth: getBarWidth(previous, max),
  };
}

export function formatCompactPrice(value: number, currency?: Currency) {
  if (Math.abs(value) < 1000) {
    return formatPrice(value, currency, { useRounding: true });
  }
  return `${formatCurrency(currency)}${formatNumber(value / 1000, 1)}k`;
}

export function formatSignedCompactPrice(value: number, currency?: Currency) {
  const sign = value < 0 ? MINUS : '+';
  return `${sign}${formatCompactPrice(Math.abs(value), currency)}`;
}

export function formatCompactDuration(seconds: number) {
  if (seconds >= SECONDS_PER_DAY) {
    return `${formatNumber(seconds / SECONDS_PER_DAY, 1)} d`;
  } else if (seconds >= SECONDS_PER_HOUR) {
    return `${formatNumber(seconds / SECONDS_PER_HOUR, 1)} h`;
  }
  return `${formatNumber(seconds / 60, 0)} m`;
}

export function formatMetricValue(
  { kind }: Pick<MetricDefinition, 'kind'>,
  value: number | null,
  currency?: Currency
) {
  if (isNullish(value)) return '--';
  if (kind === RATE) return formatPercentage(value, 1);
  if (kind === DURATION) return formatCompactDuration(value);
  if (kind === PRICE)
    return formatPrice(value, currency, { useRounding: true });
  if (kind === COMPACT_PRICE) return formatCompactPrice(value, currency);
  return formatNumber(value, 0);
}
